import { CheckBox, Icon, LoadingSpinner } from '@idexx-laboratories/spot-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import Select from 'react-select';
import Grid, { GridRendererProps } from '../../components/Grid/Grid';
import Loader from '../../components/Loader/Loader';
import { useRenderLoader } from '../../hooks/use-render-loader';
import { useStore } from '../../hooks/use-store';
import {
  DropDownOption,
  PracticeSpeciesBreeds,
  VendorSpeciesBreeds,
  type PracticeBreed,
} from '../../types/species-breeds-types';
import { isDefined } from '../../utils/object-utils';

interface ActionsType extends GridRendererProps<PracticeSpeciesBreeds> {
  action: (row: any, index: any) => void;
}

const ExpandCollapseRenderer = observer((props: ActionsType) => {
  const { row, action, expandedRowIds, index } = props;
  const [loading, startUpdate] = useRenderLoader();

  if (loading) return <LoadingSpinner size='small' />;

  const iconName = expandedRowIds?.includes(row.id) ? 'caret-up' : 'caret-down';
  const iconHint = expandedRowIds?.includes(row.id) ? 'collapse' : 'expand';

  const doAction = (): void => {
    startUpdate(() => action(row, index));
  };

  return (
    <div className='actions-cell'>
      <Icon
        role='expand-collapse-icon'
        className='expand-collapse-icon'
        iconName={iconName}
        aria-label={`species-${iconName}`}
        title={iconHint}
        onClick={doAction}
        data-testid={`expand-${row.id}-row`}
      />
    </div>
  );
});

export const UnmappedBreedsRenderer = observer(
  (props: GridRendererProps<PracticeSpeciesBreeds> & { index: number }) => {
    const { index } = props;
    const store = useStore().manageBreedsStore;
    if (store.searchText !== '')
      return <>{store.mappedSpeciesBreeds[index].breeds.length} matching results</>;
    return (
      <>
        {store.unmappedBreedsCounts[index] > 0
          ? `${store.unmappedBreedsCounts[index]} unmapped`
          : ''}
      </>
    );
  },
);

export const VendorSpeciesRenderer = observer(
  (props: GridRendererProps<PracticeSpeciesBreeds> & { index: number }) => {
    const { row } = props;
    const { globalStore } = useStore();
    const vendorSpecies = row.vendorItemId
      ? globalStore.vendorSpeciesBreeds[row.vendorItemId]
      : undefined;
    return <>{vendorSpecies?.name}</>;
  },
);

const VendorBreedRenderer = observer(
  (
    props: GridRendererProps<PracticeBreed> & {
      index: number;
      vendorSpecies?: VendorSpeciesBreeds;
    },
  ) => {
    const { row, vendorSpecies } = props;
    const { manageBreedsStore: store, globalStore } = useStore();

    const options = useMemo(
      () => (vendorSpecies ? globalStore.vendorBreedsDropdownValues[vendorSpecies.id] : []),
      [globalStore.vendorBreedsDropdownValues, vendorSpecies],
    );
    const currentOption = useMemo(
      () => options.find(o => o.value === row.vendorItemId) || null,
      [options, row.vendorItemId],
    );

    const changeBreed = (option: DropDownOption | null) => {
      runInAction(() => {
        if (!isDefined(row.vendorItemId)) {
          row.dontHide = true;
        }
        [row.vendorItemId, row.vendorBreedName] = [option?.value, option?.label];
        store.hasUnsavedChanges = true;
      });
    };

    return (
      <Select
        className='select-search'
        aria-label={`select-search-${row.id}`}
        options={options}
        value={currentOption}
        placeholder='Select Breed'
        onChange={changeBreed}
        isClearable
      />
    );
  },
);

export const SubRowRenderer = observer((props: GridRendererProps<PracticeSpeciesBreeds>) => {
  const { row } = props;
  const store = useStore().manageBreedsStore;
  const initialSpecies = store.root.globalStore.practiceSpeciesBreeds.find(s => s.id === row.id);
  const totalBreeds = initialSpecies ? initialSpecies.breeds.length : '';

  const [loading, startUpdate] = useRenderLoader();

  const toggleUnmappedBreeds = () => {
    startUpdate(() =>
      runInAction(
        () => (store.showOnlyUnmappedBreeds[row.id] = !store.showOnlyUnmappedBreeds[row.id]),
      ),
    );
  };

  return (
    <div key={`breeds-management-subrow-${row.id}`} className='subrow-wrapper'>
      <div className='subrow-info-wrapper'>
        <span className='displaying-label'>
          Displaying {row.breeds.length} of {totalBreeds}
        </span>
        <CheckBox
          id={`breed-mapping-dropdown-${row.id}`}
          label={(<Loader loading={loading}>Show mapped breeds</Loader>) as any}
          className='breed-mapping-dropdown'
          checked={store.showOnlyUnmappedBreeds[row.id] || false}
          disabled={store.searchText !== ''}
          onChange={() => toggleUnmappedBreeds()}
        />
      </div>
      <Grid
        id={`breeds-management-grid-${row.id}`}
        className='breeds-management-subrow-grid'
        columns={store.subRowColDefs(row.id)}
        data={row.breeds}
        sorting={store.sortings[row.id]}
      />
    </div>
  );
});

export const SpeciesMappingChangedMessage = (): React.JSX.Element => (
  <>
    <p>We have mapped a portion of your Cornerstone breeds to Trupanion&apos;s breed list.</p>
    <p>
      Note that Canine mixed breeds have not been mapped, and there may be other breeds requiring
      manual mapping. Use this page to make additional mappings as needed.
    </p>
    <p>
      Once satisfied, click <strong>Update Cornerstone</strong> to send this information to your
      Cornerstone database.
    </p>
  </>
);

export const expandCollapseRenderer =
  (action: (row: PracticeSpeciesBreeds) => void) =>
  (props: GridRendererProps<PracticeSpeciesBreeds>): React.JSX.Element => (
    <ExpandCollapseRenderer
      row={props.row}
      index={props.index}
      expandedRowIds={props.expandedRowIds}
      action={action}
    />
  );

export const vendorBreedRenderer =
  (vendorSpecies?: VendorSpeciesBreeds) =>
  (props: GridRendererProps<PracticeBreed>): React.JSX.Element => (
    <VendorBreedRenderer vendorSpecies={vendorSpecies} {...props} />
  );
